// web29_trial_toggle 'variation_urgency_anchor' arm — the "legit timer" offer.
//
// The 10-minute countdown on the pricing page is REAL: while it runs the user
// gets a lifetime 20% coupon on the regular (÷0.8) price — i.e. exactly today's
// charge, forever. Rules (Steven, Sep 2 2026):
//   • live     – <10 min since the offer started on the pricing page: countdown.
//   • expired  – the 10 minutes are up: the offer is gone; control's plan UI at
//                the regular price. The clock is purely time-based and keeps
//                ticking wherever the user is. A checkout session created while
//                live already carries the coupon, so it stays valid if the
//                clock runs out mid-checkout — but no NEW coupon session can be
//                created after expiry (reload / back-and-forth → regular price).
//   • revived  – arrived via an email link carrying `?offer=email`: the offer is
//                unlocked with NO countdown (emails promise it; the clock from
//                the first visit is irrelevant).
//   • reset    – 24 h after the window started (or the revival), a fresh window.
// State lives in its own localStorage key, independent of the 7-day quiz cache.
export const WEB29_OFFER_STORAGE_KEY = "BWS_WEB29_OFFER";
export const WEB29_OFFER_WINDOW_MS = 10 * 60 * 1000;
export const WEB29_OFFER_RESET_MS = 24 * 60 * 60 * 1000;
export const WEB29_OFFER_PARAM = "offer";
// Checkout is a full page load. Someone who clicks "Start Free Trial" with a
// few seconds left must not land on the regular price because the navigation
// took them past 10:00 — the checkout treats an offer that expired within
// this window as still live. The pricing page itself uses no grace.
export const WEB29_OFFER_CHECKOUT_GRACE_MS = 10 * 1000;
export const WEB29_OFFER_EMAIL_VALUE = "email";

export type Web29OfferStatus = "live" | "revived" | "expired";

export interface Web29OfferState {
  status: Web29OfferStatus;
  startedAt: number;
  expiresAt: number;
  isDiscountActive: boolean;
  showCountdown: boolean;
}

// What the checkout does for an anchor-arm user; also the Stripe
// pricing_variant suffix (urgency_anchor / urgency_anchor_email /
// urgency_anchor_expired) so the cohorts stay separable.
export type Web29AnchorCheckoutMode = "offer" | "email" | "expired";

interface StoredOffer {
  startedAt: number;
  revivedAt?: number;
}

function readStored(): StoredOffer | null {
  try {
    const raw = localStorage.getItem(WEB29_OFFER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.startedAt !== "number") return null;
    return parsed as StoredOffer;
  } catch {
    return null;
  }
}

function writeStored(stored: StoredOffer | null): void {
  try {
    if (stored) localStorage.setItem(WEB29_OFFER_STORAGE_KEY, JSON.stringify(stored));
    else localStorage.removeItem(WEB29_OFFER_STORAGE_KEY);
  } catch {
    // Storage unavailable: the offer simply behaves as freshly started.
  }
}

// The current window, starting a fresh one when none exists or when both the
// start and any revival are more than 24 h old.
function ensureWindow(now: number): StoredOffer {
  const stored = readStored();
  if (stored) {
    const latest = Math.max(stored.startedAt, stored.revivedAt ?? 0);
    if (now - latest < WEB29_OFFER_RESET_MS) return stored;
  }
  const fresh: StoredOffer = { startedAt: now };
  writeStored(fresh);
  return fresh;
}

export function getWeb29OfferState(
  now: number = Date.now(),
  graceMs: number = 0
): Web29OfferState {
  const stored = ensureWindow(now);
  const base = { startedAt: stored.startedAt, expiresAt: stored.startedAt + WEB29_OFFER_WINDOW_MS };
  if (stored.revivedAt !== undefined && now - stored.revivedAt < WEB29_OFFER_RESET_MS) {
    return { ...base, status: "revived", isDiscountActive: true, showCountdown: false };
  }
  if (now < base.expiresAt + graceMs) {
    return { ...base, status: "live", isDiscountActive: true, showCountdown: true };
  }
  return { ...base, status: "expired", isDiscountActive: false, showCountdown: false };
}

export function reviveWeb29Offer(now: number = Date.now()): Web29OfferState {
  const stored = ensureWindow(now);
  writeStored({ ...stored, revivedAt: now });
  return getWeb29OfferState(now);
}

// True while an email revival (?offer=email) is in effect. Pure read: unlike
// getWeb29OfferState it never starts a window, so it is safe to call from arm
// resolution on every render.
export function isWeb29OfferRevived(now: number = Date.now()): boolean {
  const stored = readStored();
  return !!stored && stored.revivedAt !== undefined && now - stored.revivedAt < WEB29_OFFER_RESET_MS;
}

export function toAnchorCheckoutMode(state: Web29OfferState): Web29AnchorCheckoutMode {
  if (state.status === "expired") return "expired";
  if (state.status === "revived") return "email";
  return "offer";
}

// Runs once at boot (index.tsx) so the param is honoured on ANY entry URL.
//   ?offer=email   – email retarget link: revive the offer (production feature).
//   ?offer=expired / ?offer=reset – QA knobs, honoured only while a debug /
//   forced experiment is active (forceExperiment in localStorage).
export function syncWeb29OfferFromLocation(search: string, now: number = Date.now()): void {
  let value: string | null;
  try {
    value = new URLSearchParams(search).get(WEB29_OFFER_PARAM);
  } catch {
    return;
  }
  if (!value) return;
  if (value.toLowerCase() === WEB29_OFFER_EMAIL_VALUE) {
    reviveWeb29Offer(now);
    return;
  }
  let debugging = false;
  try {
    debugging = !!localStorage.getItem("forceExperiment");
  } catch {
    debugging = false;
  }
  if (!debugging) return;
  // Land well past the checkout grace so QA sees the expired checkout too.
  if (value === "expired") {
    writeStored({ startedAt: now - WEB29_OFFER_WINDOW_MS - WEB29_OFFER_CHECKOUT_GRACE_MS - 60_000 });
  }
  else if (value === "reset") writeStored(null);
}
